import { AxiosResponse } from 'axios'
import {
  AccountRole,
  AuthUserProfile,
  ICreateOrganizationAndUserRequest,
  IForgotResetPassDto,
  IGoogleNeedsRegistration,
  ILoginRequest,
  ILoginResponse,
  MembershipInfo,
  IRegisterRequest
} from 'types/auth'
import { ISignUpRequest } from 'types/auth'
import { Where } from 'types/common'
import { api } from 'API'
import { AuthRole } from 'interfaces/auth'
import { IOrganization } from 'interfaces/organization'
import { IUser } from 'interfaces/user'
import { IGroupMember } from '../../interfaces/group/index'
import config from '../../configs'

export async function signUp(user: ISignUpRequest): Promise<ILoginResponse> {
  const createdUser: AxiosResponse = await api.post('/auth/sign-up', user)
  return createdUser.data
}

export async function countDuplicatedSignedUpUsers(where: Where<IUser> = {}): Promise<number> {
  const user: AxiosResponse = await api.get('/auth/users/count', { params: { where } })
  return user?.data?.count ?? 0
}

export async function signUpOrganization(data: ICreateOrganizationAndUserRequest): Promise<IOrganization> {
  const createdOrganization: AxiosResponse = await api.post(`/auth/organizations`, data)
  return createdOrganization.data
}

export async function countOrganizationsForSignUp(where: Where<IOrganization> = {}): Promise<number> {
  const organization: AxiosResponse = await api.get(`/auth/organizations/count?where=${JSON.stringify(where)}`)
  return organization?.data?.count ?? 0
}

/** POST /auth/check-email — check if email is already registered */
export async function checkEmail(email: string): Promise<{ exists: boolean }> {
  const response = await api.post('/auth/check-email', { email })
  return response.data
}

/** POST /auth/send-verification — send 6-digit code to primary email */
export async function sendVerificationEmail(email: string): Promise<{ sent: boolean }> {
  const response = await api.post('/auth/send-verification', { email })
  return response.data
}

/** POST /auth/verify-email — verify 6-digit code for primary email */
export async function verifyEmail(email: string, code: string): Promise<{ verified: boolean }> {
  const response = await api.post('/auth/verify-email', { email, code })
  return response.data
}

export async function login(loginData: ILoginRequest): Promise<ILoginResponse> {
  const body: Record<string, unknown> = {
    password: loginData.password
  }
  if (loginData.username) body.username = loginData.username
  if (loginData.email) body.email = loginData.email
  const response = await api.post(`/auth/login`, body)
  return response.data as ILoginResponse
}

/** POST /auth/register — standalone registration, no org required */
export async function register(data: IRegisterRequest): Promise<ILoginResponse> {
  const response = await api.post('/auth/register', data)
  return response.data as ILoginResponse
}

// SSO Authentication methods
export async function getOidcProviders(): Promise<{ providers: string[]; default: string }> {
  const response = await api.get('/auth/oidc/providers')
  return response.data
}

export function getOidcAuthUrl(provider: string, state?: string, isDemo: boolean = false): string {
  const stateParam = state || `react-${Date.now()}`

  // Use provider-specific authorization endpoint
  const endpoint = `/auth/oidc/${provider}/authorize`

  const params = new URLSearchParams()
  params.append('state', stateParam)
  if (isDemo) {
    params.append('isDemo', 'true')
  }

  return `${config.apiBaseUrl}${endpoint}?${params.toString()}`
}

export async function processOidcCallback(code: string, state: string, provider: string): Promise<ILoginResponse> {
  const endpoint = `/auth/oidc/${provider}/callback`
  const response = await api.post(endpoint, {
    code,
    state
  })
  return response.data as ILoginResponse
}

export async function getUserInfo(): Promise<AuthUserProfile> {
  const response = await api.get(`/users/me`)
  return response.data as AuthUserProfile
}

export async function getLoggedInUserGroupMembers(): Promise<IGroupMember[]> {
  try {
    const response = await api.get('/users/me/group-members')
    return response.data as IGroupMember[]
  } catch (error) {
    return []
  }
}

/** Org-agnostic: GET /users/me/memberships */
export async function getMyMemberships(): Promise<MembershipInfo[]> {
  const response = await api.get('/users/me/memberships')
  return response.data as MembershipInfo[]
}

export function requestForgotPassword(email: string, subdomain: string) {
  return api.post(`/auth/forgot-password/init`, {
    email,
    subdomain
  })
}

export function finishForgotPassword(data: IForgotResetPassDto) {
  return api.post(`/auth/forgot-password/finish`, {
    ...data
  })
}

export async function getLoggingInOrganization(subdomain: string): Promise<{
  organization: IOrganization | null
  ssoOnlyAuth: boolean
}> {
  const response: AxiosResponse = await api.get(`/auth/organizations?subdomain=${subdomain}`)
  return response.data
}

export async function getPublicSSOConfigs(subdomain: string): Promise<{
  organizationId: number
  ssoConfigs: Array<{
    provider: string
    enabled: boolean
  }>
}> {
  const response: AxiosResponse = await api.get(`/auth/sso-configs?subdomain=${subdomain}`)
  return response.data
}

export async function getAuthRoles(): Promise<AuthRole[]> {
  const response: AxiosResponse = await api.get(`/auth-roles`)
  return response.data
}

/** POST /auth/google/verify — verify Google id_token; returns login response or needsRegistration. */
export async function googleVerify(credential: string): Promise<ILoginResponse | IGoogleNeedsRegistration> {
  const response = await api.post('/auth/google/verify', { credential })
  return response.data
}

/** POST /auth/google/register — register new Google SSO user with role selection. */
export async function googleRegister(
  credential: string,
  accountType: AccountRole,
  organizationId?: number,
  invitationToken?: string
): Promise<ILoginResponse> {
  const body: {
    credential: string
    accountType: AccountRole
    organizationId?: number
    invitationToken?: string
  } = { credential, accountType }
  if (organizationId != null && organizationId > 0) {
    body.organizationId = organizationId
  }
  if (invitationToken) {
    body.invitationToken = invitationToken
  }
  const response = await api.post('/auth/google/register', body)
  return response.data as ILoginResponse
}

/** POST /auth/google/bind — link Google to the current email/password account (JWT required). */
export async function googleBind(credential: string): Promise<void> {
  await api.post('/auth/google/bind', { credential })
}

/** POST /auth/google/bind/init — start redirect bind flow (JWT). Returns opaque bindToken. */
export async function googleBindInit(returnUrl: string): Promise<{ bindToken: string }> {
  const response = await api.post('/auth/google/bind/init', { returnUrl })
  return response.data as { bindToken: string }
}

/** POST /auth/google/bind/complete — finish bind on app host (no JWT). */
export async function googleBindComplete(bindToken: string, credential: string): Promise<{ returnUrl: string }> {
  const response = await api.post('/auth/google/bind/complete', { bindToken, credential })
  return response.data as { returnUrl: string }
}
